import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Store } from 'lucide-react';
import api from '../../../utils/api';
import { apiErrorMessage } from '../../../utils/apiError';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { SUB_MERCHANT_DELETE_THRESHOLD } from '../../../utils/constants';
import { formatCount, formatNaira } from '../../../utils/money';
import { useAuth } from '../../../AuthContext';
import PageHeader from '../../shell/PageHeader';
import { Alert, Button, Card, EmptyState, PageLoader, Tabs } from '../../ui';
import SubMerchantFormModal from '../modals/SubMerchantFormModal';
import ConfirmActionModal from '../modals/ConfirmActionModal';
import OverviewTab from './sub-merchants/OverviewTab';
import PansTab from './sub-merchants/PansTab';
import UsersTab from './sub-merchants/UsersTab';
import LedgerTable from './sub-merchant/LedgerTable';
import WithdrawalsTable from './sub-merchant/WithdrawalsTable';

const LIST_PATH = '/layout/sub-merchants';

const statusChange = {
  active: {
    next: 'deactivated',
    label: 'Deactivate',
    variant: 'outline-danger',
    title: 'Deactivate sub-merchant',
    message: (name) =>
      `This signs out every user of ${name} immediately and locks them out of its screens until you reactivate it. Its PANs stay assigned and taps keep being credited. CashCard cannot withdraw its balance while it is deactivated.`,
  },
  deactivated: {
    next: 'active',
    label: 'Activate',
    variant: 'outline-primary',
    title: 'Activate sub-merchant',
    message: (name) => `This restores access for every user of ${name}. No new invite is needed.`,
  },
};

const deleteMessage = (subMerchant) => {
  const base = `This deletes ${subMerchant.name}, releases its PANs and removes its users. It cannot be undone.`;
  if (subMerchant.balance > 0) {
    return `${base} ${formatNaira(subMerchant.balance)} remaining balance will stay in your wallet.`;
  }
  return base;
};

const SubMerchantDetail = () => {
  const { subMerchantId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission(Permissions.subMerchantsUpdate);
  const canDelete = hasPermission(Permissions.subMerchantsDelete);

  const [subMerchant, setSubMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoadError(null);
    try {
      const { data } = await api.get(PartnerEndpoints.subMerchant(subMerchantId));
      if (latestRequest.current !== requestId) return;
      setSubMerchant(data.data);
    } catch (err) {
      if (latestRequest.current !== requestId) return;
      setLoadError(err);
    }
    setLoading(false);
  }, [subMerchantId]);

  const retryInitialLoad = () => {
    setLoading(true);
    load();
  };

  useEffect(() => {
    setLoading(true);
    setSubMerchant(null);
    load();
  }, [load]);

  const breadcrumbs = [
    { label: 'Home', to: '/layout/dashboard' },
    { label: 'Sub-merchants', to: LIST_PATH },
    { label: subMerchant?.name ?? 'Sub-merchant' },
  ];

  if (loading) {
    return (
      <>
        <PageLoader />
        <PageHeader title="Sub-merchant" breadcrumbs={breadcrumbs} />
      </>
    );
  }

  if (!subMerchant) {
    const notFound = loadError?.response?.status === 404;
    return (
      <>
        <PageHeader title="Sub-merchant" breadcrumbs={breadcrumbs} />
        {notFound ? (
          <Card>
            <EmptyState
              icon={<Store className="h-10 w-10" aria-hidden="true" />}
              title="Sub-merchant not found"
              message="It may have been deleted, or it belongs to another partner."
              action={
                <Link to={LIST_PATH} className="btn btn-primary">
                  Back to sub-merchants
                </Link>
              }
            />
          </Card>
        ) : (
          <Alert variant="danger">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{apiErrorMessage(loadError, 'We could not load this sub-merchant. Please try again.')}</span>
              <Button size="sm" variant="outline-danger" onClick={retryInitialLoad}>
                Retry
              </Button>
            </div>
          </Alert>
        )}
      </>
    );
  }

  const status = statusChange[subMerchant.status] ?? statusChange.active;
  const deleteBlocked = subMerchant.balance > SUB_MERCHANT_DELETE_THRESHOLD;

  const applySubMerchant = (next) => {
    latestRequest.current += 1;
    setSubMerchant(next);
  };

  const handleEdit = async (payload) => {
    const { data } = await api.put(PartnerEndpoints.subMerchant(subMerchant.id), payload);
    applySubMerchant(data.data);
  };

  const handleStatus = async () => {
    const { data } = await api.patch(PartnerEndpoints.subMerchantStatus(subMerchant.id), { status: status.next });
    applySubMerchant(data.data);
  };

  const handleDelete = async () => {
    await api.delete(PartnerEndpoints.subMerchant(subMerchant.id));
    navigate(LIST_PATH, { state: { notice: `${subMerchant.name} has been deleted.` } });
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'pans', label: `PANs (${formatCount(subMerchant.pans_count)})` },
    { id: 'users', label: `Users (${formatCount(subMerchant.users_count)})` },
    { id: 'ledger', label: 'Ledger' },
    { id: 'withdrawals', label: 'Withdrawals' },
  ];

  const panels = {
    overview: <OverviewTab subMerchant={subMerchant} />,
    pans: <PansTab subMerchant={subMerchant} canUpdate={canUpdate} onSubMerchantChange={applySubMerchant} onReload={load} />,
    users: <UsersTab subMerchant={subMerchant} canUpdate={canUpdate} onReload={load} />,
    ledger: <LedgerTable endpoint={PartnerEndpoints.subMerchantLedger(subMerchant.id)} />,
    withdrawals: <WithdrawalsTable endpoint={PartnerEndpoints.subMerchantWithdrawals(subMerchant.id)} />,
  };

  return (
    <>
      <PageHeader
        title={subMerchant.name}
        breadcrumbs={breadcrumbs}
        actions={
          (canUpdate || canDelete) && (
            <>
              {canUpdate && (
                <>
                  <Button variant="secondary" onClick={() => setEditOpen(true)}>
                    Edit
                  </Button>
                  <Button variant={status.variant} onClick={() => setStatusOpen(true)}>
                    {status.label}
                  </Button>
                </>
              )}
              {canDelete && (
                <Button variant="danger" disabled={deleteBlocked} onClick={() => setDeleteOpen(true)}>
                  Delete
                </Button>
              )}
            </>
          )
        }
      />

      {canDelete && deleteBlocked && (
        <p className="mb-4 text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          It holds {formatNaira(subMerchant.balance)}, so it cannot be deleted. Ask CashCard to withdraw the balance
          first, or deactivate it instead.
        </p>
      )}

      {loadError && (
        <Alert variant="danger" className="mb-4" onDismiss={() => setLoadError(null)}>
          {apiErrorMessage(loadError, 'We could not refresh this sub-merchant. Please reload the page.')}
        </Alert>
      )}

      <Tabs tabs={tabs} value={tab} onChange={setTab} ariaLabel="Sub-merchant sections">
        {panels[tab]}
      </Tabs>

      <SubMerchantFormModal
        isOpen={editOpen}
        subMerchant={subMerchant}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEdit}
      />

      <ConfirmActionModal
        isOpen={statusOpen}
        title={status.title}
        message={status.message(subMerchant.name)}
        confirmLabel={status.label}
        confirmVariant={status.next === 'deactivated' ? 'danger' : 'primary'}
        onClose={() => setStatusOpen(false)}
        onConfirm={handleStatus}
      />

      <ConfirmActionModal
        isOpen={deleteOpen}
        title="Delete sub-merchant"
        message={deleteMessage(subMerchant)}
        confirmLabel="Delete"
        confirmVariant="danger"
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default SubMerchantDetail;
